// Smart Links — which blog cards carry an admin external link.
//
// The interval comes from the server (admin setting), never a constant here.
// Positions are 1-based places in the list AS DISPLAYED, counted across the
// whole (filtered, sorted) list rather than the visible page, so page 2 of a
// 10-per-page list starts at position 11 and no two cards share a position.
// The same rule runs on the server (services/smartLinkService.js), which is
// what actually picks the destination.

export function isEligibleCard(position, interval) {
  return (
    Number.isInteger(position) &&
    Number.isInteger(interval) &&
    interval > 0 &&
    position > 0 &&
    position % interval === 0
  );
}

// Index into the active-link list (creation order) for a card, or -1.
export function linkIndexFor(position, interval, linkCount) {
  if (!linkCount || !isEligibleCard(position, interval)) return -1;
  return (position / interval - 1) % linkCount;
}

// 1-based position in the whole list of the `indexOnPage`-th visible card.
export function listPosition(indexOnPage, currentPage, perPage) {
  return (Math.max(1, currentPage) - 1) * perPage + indexOnPage + 1;
}

// Clicks on a card's own controls (like, comment, share, follow, author link,
// read more, image/video, inputs, menus) keep doing exactly what they do and
// never start an external redirect. `.blog-actions` (the like/comment/share
// bar on all three pages) and `.cmt-box` (the open comments panel) are
// excluded whole, so a click in the gap between two buttons or on a comment's
// text is not a card click either.
const INTERACTIVE =
  'a, button, input, textarea, select, option, label, summary, video, audio, img, iframe, [role="button"], [role="menu"], [role="menuitem"], [contenteditable="true"], .blog-actions, .cmt-box, [data-smart-link-ignore]';

export function isInteractiveTarget(target, card) {
  let el = target && target.nodeType === 1 ? target : target?.parentElement;
  while (el && el !== card) {
    if (el.matches && el.matches(INTERACTIVE)) return true;
    el = el.parentElement;
  }
  return false;
}

// Exception to the above: the like / comment / share buttons in the action bar
// DO open the smart link on an eligible card's first click (once per cooldown)
// instead of liking / commenting / sharing. During the cooldown they work as
// normal. Returns that button, or null.
const REDIRECT_ACTIONS = ".heart-btn, .comment-btn, .share-btn";

export function redirectActionButton(target, card) {
  let el = target && target.nodeType === 1 ? target : target?.parentElement;
  while (el && el !== card) {
    if (el.matches && el.matches(REDIRECT_ACTIONS)) {
      return el.closest(".blog-actions") && card.contains(el) ? el : null;
    }
    el = el.parentElement;
  }
  return null;
}

// `serverOffsetMs` = server clock − browser clock, so a wrong device clock
// can't end (or stretch) a cooldown early. The server re-checks regardless.
export function cooldownActive(cooldownUntil, serverOffsetMs = 0, now = Date.now()) {
  if (!cooldownUntil) return false;
  const until = new Date(cooldownUntil).getTime();
  return Number.isFinite(until) && now + serverOffsetMs < until;
}
